/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {Subscription, switchMap, takeWhile, timer} from 'rxjs';
import {EsciComponent} from '../esci.component';
import {FileDataZipImportStatus, isFileDataZipImportFinished} from '../../model/fileDataZipImport.model';

/**
 * IT Admin Tools -> Import Files (Zip).
 *
 * Uploads a zip and has the backend import every file in it into file_data as a background job, then polls
 * the job for per-bucket counts (imported, failed) and shows the per-file results when it ends.
 */
@Component({
    selector: 'app-import-file-data-zip',
    templateUrl: './import-file-data-zip.component.html',
    styleUrls: ['./import-file-data-zip.component.scss'],
    standalone: false
})
export class ImportFileDataZipComponent extends EsciComponent implements OnInit, OnDestroy {
    static readonly POLL_INTERVAL_MS = 2000;

    permissionUrl = '/esciUI/esciITAdminPermission';

    readonly selectedFile = signal<File | null>(null);
    /** Upload percentage while the zip is being sent; null when no upload is in flight. */
    readonly uploadPercent = signal<number | null>(null);
    readonly jobStatus = signal<FileDataZipImportStatus | null>(null);
    readonly errorMessage = signal('');

    readonly uploading = computed(() => this.uploadPercent() !== null);
    readonly finished = computed(() => isFileDataZipImportFinished(this.jobStatus()));
    readonly running = computed(() => this.uploading() || (this.jobStatus() !== null && !this.finished()));
    readonly processedCount = computed(() => {
        const status = this.jobStatus();
        return status ? status.succeededCount + status.failedCount : 0;
    });
    readonly progressPercent = computed(() => {
        const total = this.jobStatus()?.totalFiles;
        if (!total) {
            return this.finished() ? 100 : 0;
        }
        return Math.floor(this.processedCount() * 100 / total);
    });

    private uploadSubscription?: Subscription;
    private pollSubscription?: Subscription;

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.checkPermission(this);
    }

    ngOnDestroy(): void {
        this.uploadSubscription?.unsubscribe();
        this.pollSubscription?.unsubscribe();
    }

    onZipSelected(files: File[]): void {
        this.selectedFile.set(files?.length === 1 ? files[0] : null);
    }

    startImport(): void {
        const file = this.selectedFile();
        if (!file || this.running()) {
            return;
        }
        this.errorMessage.set('');
        this.jobStatus.set(null);
        this.uploadPercent.set(0);
        this.uploadSubscription?.unsubscribe();
        this.uploadSubscription = this.esciService.startFileDataZipImport(file).subscribe({
            next: (event) => {
                if (event.type === HttpEventType.UploadProgress && event.total) {
                    this.uploadPercent.set(Math.round(event.loaded * 100 / event.total));
                } else if (event instanceof HttpResponse && event.body?.jobId) {
                    this.uploadPercent.set(null);
                    this.pollJob(event.body.jobId);
                }
            },
            error: (err) => {
                this.uploadPercent.set(null);
                this.errorMessage.set(err?.error?.message ?? 'The zip file could not be uploaded.');
            }
        });
    }

    private pollJob(jobId: string): void {
        this.pollSubscription?.unsubscribe();
        this.pollSubscription = timer(0, ImportFileDataZipComponent.POLL_INTERVAL_MS).pipe(
            switchMap(() => this.esciService.getFileDataZipImportStatus(jobId)),
            takeWhile((status) => !isFileDataZipImportFinished(status), true)
        ).subscribe({
            next: (status) => this.jobStatus.set(status),
            error: () => {
                this.jobStatus.set(null);
                this.errorMessage.set('Lost contact with the import job. It may still be running on the server; '
                    + 'check the file data before importing the same zip again.');
            }
        });
    }
}
